/**
 * LCOE-EXPLAIN-1 (2026-07-03) - shared derivation + composer for the LCOE
 * Cost Comparison exhibit write-up.
 *
 * William: "LCOE Cost Comparison has no write-up of explanation?" - every
 * surface that renders the exhibit (view/PDF chart section, DOCX, PPTX) now
 * carries the SAME deterministic analysis paragraph, composed ONLY from the
 * chart's own values (corrected LCOE bands, published industry-norm
 * midpoints, the grid retail reference line). No generated text, nothing
 * invented - this module is the single source for that text so the three
 * surfaces can never drift apart.
 *
 * Derivation mirrors LcoeChartSection's row build (corrected → ±BAND_PCT
 * band; else industry-norm baseline flagged as benchmark; else pending) -
 * the component consumes THIS module for its explainer so text parity is
 * structural, not coincidental.
 */

import { normalizeElectricityPriceToCents } from '../reportFormatters';
import {
  resolveReportCorrectedLcoe,
  correctedLcoeForBucket,
  type LcoeTechBucket,
} from '../finDecision/reportLcoeSurface';
import { INDUSTRY_BASELINES, type BaselineKey } from '../../services/financial/industryBaselines';
import type { SiteRagReport } from '../../types/siteRag';

export interface LcoeExplainerRow {
  tech: string;
  low: number;
  high: number;
  pending: boolean;
  industryNorm: boolean;
}

/** Visual half-width of the band around a corrected midpoint (±%) - MUST match LcoeChartSection. */
const BAND_PCT = 0.08;

// MUST mirror LcoeChartSection's ROW_DEFS + BUCKET_BASELINE_KEY exactly.
const ROW_DEFS: Array<{ tech: string; bucket: LcoeTechBucket; baselineKey: BaselineKey }> = [
  { tech: 'Nuclear SMR', bucket: 'nuclear', baselineKey: 'smr-noak' },
  { tech: 'Natural Gas', bucket: 'gas', baselineKey: 'gas-cc' },
  { tech: 'Solar + BESS', bucket: 'solar', baselineKey: 'solar-bess' },
  { tech: 'Wind + BESS', bucket: 'wind', baselineKey: 'wind-bess' },
];

export function isLcoeUnavailableInFrozenSource(report: SiteRagReport): boolean {
  const corrected = resolveReportCorrectedLcoe(report);
  const hasSiteComputed = ROW_DEFS.some(
    ({ bucket }) => correctedLcoeForBucket(corrected, bucket).midUsdPerMwh != null,
  );
  if (hasSiteComputed) return false;

  const sourceCoverage = report.screeningBasis?.sourceCoverage;
  if (
    Array.isArray(sourceCoverage) &&
    sourceCoverage.some(
      (entry) =>
        entry != null &&
        typeof entry === 'object' &&
        (entry as { section?: string }).section === 'lcoe' &&
        (entry as { status?: string }).status === 'unavailable',
    )
  ) {
    return true;
  }

  const omissions = (
    report as unknown as {
      reportRevision?: {
        document?: { omissions?: Array<{ sectionId?: string; reason?: string }> };
      };
    }
  ).reportRevision?.document?.omissions;
  if (
    Array.isArray(omissions) &&
    omissions.some((o) => o != null && o.sectionId === 'lcoeChart')
  ) {
    return true;
  }

  return false;
}

export function deriveLcoeExplainerRows(report: SiteRagReport): {
  rows: LcoeExplainerRow[];
  gridLineUsdPerMwh: number | null;
} {
  const ds = report.datasets;
  const gridRate = normalizeElectricityPriceToCents(ds?.electricity ?? {});
  const gridLineUsdPerMwh = gridRate != null ? Math.round(gridRate * 10 * 10) / 10 : null;

  if (isLcoeUnavailableInFrozenSource(report)) {
    return {
      rows: ROW_DEFS.map(({ tech }) => ({
        tech,
        low: 0,
        high: 0,
        pending: true,
        industryNorm: false,
      })),
      gridLineUsdPerMwh,
    };
  }

  const corrected = resolveReportCorrectedLcoe(report);
  const rows: LcoeExplainerRow[] = ROW_DEFS.map(({ tech, bucket, baselineKey }) => {
    const entry = correctedLcoeForBucket(corrected, bucket);
    const mid = entry.midUsdPerMwh;
    if (mid != null) {
      return {
        tech,
        low: Math.round(mid * (1 - BAND_PCT)),
        high: Math.round(mid * (1 + BAND_PCT)),
        pending: false,
        industryNorm: false,
      };
    }
    const norm = INDUSTRY_BASELINES[baselineKey];
    const normMid = norm?.values?.lcoeNominalPerMwhUsd;
    if (typeof normMid === 'number' && Number.isFinite(normMid)) {
      return {
        tech,
        low: Math.round(normMid * (1 - BAND_PCT)),
        high: Math.round(normMid * (1 + BAND_PCT)),
        pending: false,
        industryNorm: true,
      };
    }
    return { tech, low: 0, high: 0, pending: true, industryNorm: false };
  });
  return { rows, gridLineUsdPerMwh };
}

export function composeLcoeExplainer(
  rows: readonly LcoeExplainerRow[],
  gridLineUsdPerMwh: number | null,
): string {
  const finiteGridLine = gridLineUsdPerMwh != null && Number.isFinite(gridLineUsdPerMwh) && gridLineUsdPerMwh >= 0
    ? gridLineUsdPerMwh : null;
  const corrected = rows.filter((r) => !r.pending && !r.industryNorm);
  const baseline = rows.filter((r) => r.industryNorm);
  const pendingRows = rows.filter((r) => r.pending);
  const mid = (r: LcoeExplainerRow): number => Math.round((r.low + r.high) / 2);
  const money = (n: number): string => `$${Math.round(n)}/MWh`;
  const parts: string[] = [];

  if (corrected.length > 0) {
    const ranked = [...corrected].sort((a, b) => mid(a) - mid(b));
    const best = ranked[0]!;
    let lead = `Among the site-computed options, ${best.tech} carries the lowest levelized cost at roughly ${money(best.low)}–${money(best.high)}`;
    if (finiteGridLine != null) {
      const rel = mid(best) < finiteGridLine ? 'below' : 'above';
      lead += `, ${rel} the electricity price reference benchmark of ${money(finiteGridLine)}`;
    }
    parts.push(lead + '.');
    if (ranked.length > 1) {
      const worst = ranked[ranked.length - 1]!;
      parts.push(
        `${worst.tech} is the most expensive site-computed pathway at ${money(worst.low)}–${money(worst.high)}, a spread of about ${money(mid(worst) - mid(best)).replace('/MWh', ' per MWh')} against ${best.tech}.`,
      );
    }
  } else if (finiteGridLine != null) {
    parts.push(`The electricity price reference benchmark is ${money(finiteGridLine)}.`);
  }

  if (baseline.length > 0) {
    const names = baseline.map((r) => r.tech).join(', ');
    const midFor = (tech: string, fallback: number): number => {
      const found = baseline.find((r) => r.tech === tech);
      return found ? mid(found) : fallback;
    };
    const smrMid = midFor('Nuclear SMR', Math.round(INDUSTRY_BASELINES['smr-noak'].values.lcoeNominalPerMwhUsd ?? 65));
    const gasMid = midFor('Natural Gas', Math.round(INDUSTRY_BASELINES['gas-cc'].values.lcoeNominalPerMwhUsd ?? 58));
    const solarMid = midFor('Solar + BESS', Math.round(INDUSTRY_BASELINES['solar-bess'].values.lcoeNominalPerMwhUsd ?? 48));
    const windMid = midFor('Wind + BESS', Math.round(INDUSTRY_BASELINES['wind-bess'].values.lcoeNominalPerMwhUsd ?? 42));
    parts.push(
      `${names} ${baseline.length === 1 ? 'shows' : 'show'} illustrative reference midpoints (marked *, drawn with non-evidentiary ±8% graphical display markers around NREL ATB 2024 / Lazard LCOE+ v17.0 / EIA AEO 2024 nominal 2024$ midpoint estimates rather than sourced low/high range endpoints) pending a site-specific calculation; ${baseline.length === 1 ? 'it is a generic reference benchmark' : 'they are generic reference benchmarks'}, not site-computed figures (Nuclear SMR reflects a steady-state nth-of-a-kind target [~$${smrMid}/MWh] rather than first-of-a-kind deployment [~$89–102/MWh], Natural Gas reflects a generic CCGT baseline [~$${gasMid}/MWh] excluding site lateral/firming costs, and Solar + BESS [~$${solarMid}/MWh] and Wind + BESS [~$${windMid}/MWh] reflect standalone generation-plus-4-hour-storage benchmarks rather than 24/7 firmed AI baseload with overbuild and multi-day firming).`,
    );
  }
  if (pendingRows.length > 0) {
    parts.push(`No cost basis is available yet for ${pendingRows.map((r) => r.tech).join(', ')}.`);
  }
  if (finiteGridLine != null) {
    parts.push('This reference does not establish an applicable site tariff. Compare the served-load LCOE with the actual all-in tariff, including demand, delivery and time-of-use charges, for the same load and service conditions.');
  }
  return parts.join(' ');
}
